/**
 * Prepared legal Markdown, rendered as inert evidence: headings, paragraphs,
 * lists, quotes, code, rules, tables, emphasis and links. No HTML is
 * interpreted. Images and same-origin links are limited to the media and
 * prepared-document routes; other external links open separately and show
 * their address.
 */
import type { JSX, ReactNode } from 'react'
import { safeExternalHttpUrl, safeSameOriginPath } from '../../../api/legal'

function safeMarkdownUrl(raw: string, baseUrl?: string, image = false): string | null {
  if (!raw || /[\u0000-\u001f\u007f\\]/.test(raw)) return null
  const allowedPath = (path: string | null) => path !== null && (path.startsWith('/media/') || path.startsWith('/api/legal-pack-documents/'))
  const external = safeExternalHttpUrl(raw)
  if (external) {
    const url = new URL(external)
    if (url.origin === window.location.origin) {
      const path = safeSameOriginPath(`${url.pathname}${url.search}${url.hash}`)
      return allowedPath(path) ? path : null
    }
    return image ? null : external
  }
  if (/^[a-z][a-z\d+.-]*:/i.test(raw)) return null
  if (raw.startsWith('/')) {
    const path = safeSameOriginPath(raw)
    return allowedPath(path) ? path : null
  }
  if (!baseUrl) return null
  try {
    const basePath = safeSameOriginPath(baseUrl)
    if (!basePath) return null
    const resolved = new URL(raw, new URL(basePath, window.location.origin))
    const path = resolved.origin === window.location.origin ? safeSameOriginPath(`${resolved.pathname}${resolved.search}${resolved.hash}`) : null
    return allowedPath(path) ? path : null
  } catch {
    return null
  }
}

function markdownLink(value: string): { image: boolean; label: string; url: string } | null {
  const match = /^(!?)\[([^\]]*)\]\(([^)\s]+)\)$/.exec(value)
  return match ? { image: match[1] === '!', label: match[2] ?? '', url: match[3] ?? '' } : null
}

function inline(text: string, keyPrefix: string, baseUrl?: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const token = /(!?\[[^\]]*\]\([^)\s]+\)|\*\*[^*]+\*\*|`[^`]+`)/g
  let cursor = 0
  let match: RegExpExecArray | null
  while ((match = token.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index))
    const value = match[0]
    const key = `${keyPrefix}-${match.index}`
    if (value.startsWith('[')) {
      const link = markdownLink(value)
      const href = link ? safeMarkdownUrl(link.url, baseUrl) : null
      const external = href !== null && /^https?:\/\//.test(href)
      nodes.push(href ? <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">{link?.label}{external ? <> ↗ <span>({href})</span></> : null}</a> : value)
    } else if (value.startsWith('!')) {
      const link = markdownLink(value)
      const src = link ? safeMarkdownUrl(link.url, baseUrl, true) : null
      nodes.push(src ? <img className="md-inline-image" key={key} src={src} alt={link?.label ?? ''} loading="lazy" decoding="async" /> : value)
    } else if (value.startsWith('**')) {
      nodes.push(<span className="md-emphasis" key={key}>{value.slice(2, -2)}</span>)
    } else {
      nodes.push(<code key={key}>{value.slice(1, -1)}</code>)
    }
    cursor = match.index + value.length
  }
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

function cells(line: string): string[] { return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim()) }
function isTableDivider(line: string): boolean { const parts = cells(line); return parts.length > 0 && parts.every(part => /^:?-{3,}:?$/.test(part)) }

function isBlockStart(lines: string[], index: number): boolean {
  const line = lines[index] ?? ''
  return /^(#{1,6})\s+/.test(line) || /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line) || /^>\s?/.test(line) || /^```/.test(line)
    || /^\s*(?:---+|___+)\s*$/.test(line) || (line.includes('|') && isTableDivider(lines[index + 1] ?? ''))
}

export function MarkdownDocument({ source, omitFirstHeading, baseUrl }: { source: string; omitFirstHeading?: string; baseUrl?: string }) {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const at = (i: number) => lines[i] ?? ''
  const firstContent = lines.findIndex(line => line.trim().length > 0)
  const firstHeading = firstContent >= 0 ? /^#\s+(.+)$/.exec(at(firstContent)) : null
  if (firstHeading && omitFirstHeading && (firstHeading[1] ?? '').trim() === omitFirstHeading.trim()) lines.splice(firstContent, 1)
  const blocks: ReactNode[] = []
  let index = 0
  while (index < lines.length) {
    const line = at(index)
    if (!line.trim()) { index += 1; continue }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line)
    if (heading) {
      const level = firstHeading && omitFirstHeading ? Math.min((heading[1] ?? '').length + 1, 6) : (heading[1] ?? '').length
      const Heading = `h${level}` as keyof JSX.IntrinsicElements
      blocks.push(<Heading key={`heading-${index}`}>{inline(heading[2] ?? '', `heading-${index}`, baseUrl)}</Heading>)
      index += 1
      continue
    }

    const image = markdownLink(line.trim())
    if (image?.image) {
      const src = safeMarkdownUrl(image.url, baseUrl, true)
      blocks.push(src ? <figure className="md-image" key={`image-${index}`}><img src={src} alt={image.label} loading="lazy" decoding="async" />{image.label ? <figcaption>{image.label}</figcaption> : null}</figure> : <p key={`image-text-${index}`}>{line.trim()}</p>)
      index += 1
      continue
    }

    if (line.includes('|') && isTableDivider(at(index + 1))) {
      const headings = cells(line)
      const rows: string[][] = []
      index += 2
      while (index < lines.length && at(index).includes('|') && at(index).trim()) { rows.push(cells(at(index))); index += 1 }
      blocks.push(<div className="md-table" key={`table-${index}`}><table>
        <thead><tr>{headings.map((value, cellIndex) => <th key={cellIndex}>{inline(value, `th-${index}-${cellIndex}`, baseUrl)}</th>)}</tr></thead>
        <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{headings.map((_, cellIndex) => <td key={cellIndex}>{inline(row[cellIndex] ?? '', `td-${index}-${rowIndex}-${cellIndex}`, baseUrl)}</td>)}</tr>)}</tbody>
      </table></div>)
      continue
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = []
      while (index < lines.length && /^\s*[-*]\s+/.test(at(index))) { items.push(at(index).replace(/^\s*[-*]\s+/, '')); index += 1 }
      blocks.push(<ul key={`list-${index}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{inline(item, `li-${index}-${itemIndex}`, baseUrl)}</li>)}</ul>)
      continue
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (index < lines.length && /^\s*\d+\.\s+/.test(at(index))) { items.push(at(index).replace(/^\s*\d+\.\s+/, '')); index += 1 }
      blocks.push(<ol key={`ordered-${index}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{inline(item, `oli-${index}-${itemIndex}`, baseUrl)}</li>)}</ol>)
      continue
    }

    if (/^>\s?/.test(line)) {
      const quote: string[] = []
      while (index < lines.length && /^>\s?/.test(at(index))) { quote.push(at(index).replace(/^>\s?/, '')); index += 1 }
      blocks.push(<blockquote key={`quote-${index}`}>{inline(quote.join(' '), `quote-${index}`, baseUrl)}</blockquote>)
      continue
    }

    if (/^```/.test(line)) {
      const code: string[] = []
      index += 1
      while (index < lines.length && !/^```/.test(at(index))) { code.push(at(index)); index += 1 }
      if (index < lines.length) index += 1
      blocks.push(<pre key={`code-${index}`}><code>{code.join('\n')}</code></pre>)
      continue
    }

    if (/^\s*(?:---+|___+)\s*$/.test(line)) { blocks.push(<hr key={`rule-${index}`} />); index += 1; continue }

    const paragraph = [line.trim()]
    index += 1
    while (index < lines.length && at(index).trim() && !isBlockStart(lines, index)) { paragraph.push(at(index).trim()); index += 1 }
    blocks.push(<p key={`paragraph-${index}`}>{inline(paragraph.join(' '), `paragraph-${index}`, baseUrl)}</p>)
  }
  return <article className="md-doc">{blocks}</article>
}
